// app/frontend/pages/Community/Space/Show.tsx
import { Link, usePage } from "@inertiajs/react";
import {
  Box,
  Text,
  Card,
  Group,
  Stack,
  Avatar,
  NavLink,
  ScrollArea,
  Divider,
  ActionIcon,
  Drawer,
  Burger,
  Button,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
  IconMessageCircle,
  IconPin,
  IconLayoutList,
  IconBell,
  IconVideo,
  IconPlus,
} from "@tabler/icons-react";
import { SEO } from "../../../components/common/SEO";
import { L2KText, L2KH4, L2KCaption, L2KBadge } from "../../../components/ui/Typography";
import { PostContent } from "../../../components/community/PostContent";

interface User {
  id: number;
  name: string;
  username: string;
  avatar_url: string | null;
}

interface CurrentUser {
  id: number;
  name: string;
  email: string;
  avatar_url: string | null;
  admin: boolean;
}

interface Space {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  emoji: string;
  posts_count: number;
  category?: string | null;
}

interface Post {
  id: number;
  title: string;
  body: string;
  pinned: boolean;
  comments_count: number;
  reactions_count: number;
  last_activity_at: string;
  created_at: string;
  user: User;
  user_reaction?: string | null;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
}

interface Props {
  space: Space;
  posts: Post[];
  pagination: Pagination;
  spaces: Space[];
  can_admin: boolean;
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

function PostCard({ post, spaceSlug, isDesktop }: { post: Post; spaceSlug: string; isDesktop: boolean }) {
  return (
    <Link
      href={`/community/spaces/${spaceSlug}/posts/${post.id}`}
      style={{ textDecoration: "none" }}
    >
      <Card
        padding={isDesktop ? "md" : "sm"}
        radius={isDesktop ? "md" : "sm"}
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.03)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          backdropFilter: "blur(10px)",
          cursor: "pointer",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "rgba(255, 152, 0, 0.3)";
          e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
          e.currentTarget.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.08)";
          e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.03)";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <Stack gap={isDesktop ? "xs" : 6}>
          {/* Post Header */}
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap={isDesktop ? "sm" : "xs"} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
              <Avatar
                src={post.user.avatar_url}
                alt={post.user.name}
                radius="xl"
                size={isDesktop ? "sm" : "xs"}
                color="orange"
                style={{ flexShrink: 0 }}
              >
                {post.user.name.charAt(0)}
              </Avatar>
              <Box style={{ minWidth: 0, flex: 1 }}>
                <L2KText size="xs" style={{ fontWeight: 500, fontSize: isDesktop ? 13 : 12 }}>
                  {post.user.name}
                </L2KText>
                <L2KCaption variant="tertiary" style={{ fontSize: isDesktop ? 11 : 10 }}>
                  {formatRelativeTime(post.created_at)}
                </L2KCaption>
              </Box>
            </Group>
            {post.pinned && (
              <L2KBadge
                variant="subtle"
                size="xs"
                leftSection={<IconPin size={9} />}
                style={{ flexShrink: 0, fontSize: 10 }}
              >
                Pinned
              </L2KBadge>
            )}
          </Group>

          {/* Post Content */}
          <Box>
            <L2KText size={isDesktop ? "sm" : "xs"} style={{ fontWeight: 600, marginBottom: 2, fontSize: isDesktop ? 14 : 13 }}>
              {post.title}
            </L2KText>
            <PostContent body={post.body} size="xs" maxPreviews={1} />
          </Box>

          {/* Post Footer */}
          <Group gap={isDesktop ? "md" : "sm"} mt={2}>
            <Group gap={4}>
              <IconMessageCircle size={isDesktop ? 14 : 12} color="#B8B8B8" />
              <L2KCaption variant="tertiary" style={{ fontSize: isDesktop ? 11 : 10 }}>
                {post.comments_count} {post.comments_count === 1 ? "comment" : "comments"}
              </L2KCaption>
            </Group>
            {post.reactions_count > 0 && (
              <L2KCaption variant="tertiary" style={{ fontSize: isDesktop ? 11 : 10 }}>
                {post.reactions_count} {post.reactions_count === 1 ? "reaction" : "reactions"}
              </L2KCaption>
            )}
          </Group>
        </Stack>
      </Card>
    </Link>
  );
}

interface SidebarContentProps {
  spaces: Space[];
  currentSpaceSlug: string;
  currentUser: CurrentUser;
  onNavClick?: () => void;
}

function SidebarContent({ spaces, currentSpaceSlug, currentUser, onNavClick }: SidebarContentProps) {
  // Group spaces by category
  const spacesByCategory: Record<string, Space[]> = {};
  spaces.forEach((space) => {
    const category = space.category || "Other";
    if (!spacesByCategory[category]) {
      spacesByCategory[category] = [];
    }
    spacesByCategory[category].push(space);
  });

  const categoryOrder = ["Welcome", "Community", "Resources", "Other"];

  return (
    <Box
      style={{
        backgroundColor: "#1a1a1a",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Logo/Header */}
      <Box px="md" py="lg" style={{ borderBottom: "1px solid #2a2a2a" }}>
        <Link href="/" style={{ textDecoration: "none" }} onClick={onNavClick}>
          <Text
            size="xl"
            fw={700}
            style={{
              background: "linear-gradient(135deg, #f97316, #ea580c)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Learntokiz
          </Text>
        </Link>
      </Box>

      {/* Navigation */}
      <ScrollArea flex={1} px="sm" py="md">
        <Stack gap={4}>
          {/* Back to Learning */}
          <NavLink
            component={Link}
            href="/videos"
            label="Learn"
            leftSection={<IconVideo size={18} />}
            onClick={onNavClick}
            styles={{
              root: {
                borderRadius: 8,
                fontWeight: 500,
                color: "#e5e5e5",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                },
              },
              label: {
                color: "#e5e5e5",
              },
            }}
          />

          <Divider my="sm" color="#2a2a2a" />

          {/* Feed Link */}
          <NavLink
            component={Link}
            href="/community"
            label="Feed"
            leftSection={<IconLayoutList size={18} />}
            onClick={onNavClick}
            styles={{
              root: {
                borderRadius: 8,
                fontWeight: 500,
                color: "#e5e5e5",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                },
              },
              label: {
                color: "#e5e5e5",
              },
            }}
          />

          <Divider my="sm" color="#2a2a2a" />

          {/* Spaces by Category */}
          {categoryOrder.map((category) => {
            const categorySpaces = spacesByCategory[category];
            if (!categorySpaces || categorySpaces.length === 0) return null;

            return (
              <Box key={category} mb="md">
                <Text
                  size="xs"
                  fw={600}
                  c="#737373"
                  tt="uppercase"
                  px="sm"
                  mb="xs"
                >
                  {category}
                </Text>
                {categorySpaces.map((space) => (
                  <NavLink
                    key={space.id}
                    component={Link}
                    href={`/community/spaces/${space.slug}`}
                    label={space.name}
                    leftSection={<Text size="sm">{space.emoji}</Text>}
                    active={space.slug === currentSpaceSlug}
                    onClick={onNavClick}
                    styles={{
                      root: {
                        borderRadius: 8,
                        color: "#e5e5e5",
                        "&[data-active]": {
                          backgroundColor: "rgba(249, 115, 22, 0.15)",
                          color: "#f97316",
                        },
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                        },
                      },
                      label: {
                        color: "inherit",
                      },
                    }}
                  />
                ))}
              </Box>
            );
          })}
        </Stack>
      </ScrollArea>

      {/* User Section */}
      <Box px="md" py="md" style={{ borderTop: "1px solid #2a2a2a" }}>
        <Group>
          <Avatar
            src={currentUser.avatar_url}
            alt={currentUser.name}
            radius="xl"
            size="sm"
            color="orange"
          >
            {currentUser.name.charAt(0)}
          </Avatar>
          <Box flex={1} style={{ minWidth: 0 }}>
            <L2KText size="xs" style={{ fontWeight: 500 }}>
              {currentUser.name}
            </L2KText>
          </Box>
        </Group>
      </Box>
    </Box>
  );
}

export default function SpaceShow({ space, posts, pagination, spaces, can_admin }: Props) {
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const isDesktop = useMediaQuery("(min-width: 992px)");
  const { current_user } = usePage<{ current_user: CurrentUser }>().props;

  return (
    <Box
      style={{
        minHeight: "100vh",
        background: `
          radial-gradient(ellipse at 20% 50%, rgba(255, 152, 0, 0.15) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 20%, rgba(255, 193, 7, 0.12) 0%, transparent 50%),
          radial-gradient(ellipse at 40% 80%, rgba(255, 87, 34, 0.1) 0%, transparent 50%),
          linear-gradient(135deg, #171717 0%, #1a1a1a 25%, #171717 50%, #1f1f1f 75%, #171717 100%)
        `,
      }}
    >
      <SEO
        title={`${space.name} - Community - Learntokiz`}
        description={
          space.description || `${space.name} community on Learntokiz`
        }
        url={`https://learntokiz.io/community/${space.slug}`}
      />

      {/* Mobile Drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size={280}
        padding={0}
        withCloseButton={false}
        styles={{
          body: {
            padding: 0,
            height: "100%",
          },
          content: {
            backgroundColor: "#1a1a1a",
          },
        }}
      >
        <SidebarContent
          spaces={spaces}
          currentSpaceSlug={space.slug}
          currentUser={current_user}
          onNavClick={closeDrawer}
        />
      </Drawer>

      {/* Desktop Sidebar */}
      {isDesktop && (
        <Box
          style={{
            width: 280,
            position: "fixed",
            left: 0,
            top: 0,
            height: "100vh",
            borderRight: "1px solid #2a2a2a",
            zIndex: 100,
          }}
        >
          <SidebarContent
            spaces={spaces}
            currentSpaceSlug={space.slug}
            currentUser={current_user}
          />
        </Box>
      )}

      {/* Mobile Header */}
      {!isDesktop && (
        <Box
          px="md"
          py="sm"
          style={{
            backgroundColor: "#1a1a1a",
            borderBottom: "1px solid #2a2a2a",
            position: "sticky",
            top: 0,
            zIndex: 50,
          }}
        >
          <Group justify="space-between" align="center">
            <Group gap="sm">
              <Burger
                opened={drawerOpened}
                onClick={openDrawer}
                size="sm"
                color="#e5e5e5"
              />
              <Link href="/" style={{ textDecoration: "none" }}>
                <Text
                  size="lg"
                  fw={700}
                  style={{
                    background: "linear-gradient(135deg, #f97316, #ea580c)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Learntokiz
                </Text>
              </Link>
            </Group>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              style={{ color: "#a3a3a3" }}
            >
              <IconBell size={20} />
            </ActionIcon>
          </Group>
        </Box>
      )}

      {/* Main Content */}
      <Box
        style={{
          marginLeft: isDesktop ? 280 : 0,
          padding: isDesktop ? "2rem" : "0.75rem",
          maxWidth: isDesktop ? 800 : "100%",
        }}
      >
        <Stack gap={isDesktop ? "lg" : "sm"}>
          {/* Header */}
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap={isDesktop ? "md" : "sm"}>
            <Box style={{ minWidth: 0, flex: 1 }}>
              <Group gap={isDesktop ? "sm" : "xs"} align="center">
                <Text size={isDesktop ? "1.25rem" : "1rem"}>{space.emoji}</Text>
                <L2KText size={isDesktop ? "lg" : "md"} style={{ fontWeight: 600 }}>{space.name}</L2KText>
              </Group>
              {space.description && (
                <L2KText size="xs" variant="tertiary" style={{ marginTop: 4 }}>
                  {space.description}
                </L2KText>
              )}
            </Box>
            <Link href={`/community/spaces/${space.slug}/posts/new`}>
              <Button
                leftSection={<IconPlus size={16} />}
                size={isDesktop ? "sm" : "xs"}
                style={{
                  background: "linear-gradient(135deg, #f97316, #ea580c)",
                }}
              >
                New Post
              </Button>
            </Link>
          </Group>

          <Divider color="#2a2a2a" />

          {/* Posts Feed */}
          <Stack gap="sm">
            {posts.length === 0 ? (
              <Card
                padding="xl"
                radius="md"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  backdropFilter: "blur(10px)",
                  textAlign: "center",
                }}
              >
                <L2KText size="sm" variant="tertiary">
                  No posts yet. Be the first to start a discussion!
                </L2KText>
              </Card>
            ) : (
              posts.map((post) => (
                <PostCard key={post.id} post={post} spaceSlug={space.slug} isDesktop={isDesktop ?? false} />
              ))
            )}
          </Stack>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <Group justify="center" mt="lg">
              <Button
                variant="subtle"
                disabled={pagination.current_page === 1}
                component={Link}
                href={`/community/spaces/${space.slug}?page=${pagination.current_page - 1}`}
                style={{ color: "#a3a3a3" }}
              >
                Previous
              </Button>
              <L2KCaption variant="tertiary">
                Page {pagination.current_page} of {pagination.total_pages}
              </L2KCaption>
              <Button
                variant="subtle"
                disabled={pagination.current_page === pagination.total_pages}
                component={Link}
                href={`/community/spaces/${space.slug}?page=${pagination.current_page + 1}`}
                style={{ color: "#a3a3a3" }}
              >
                Next
              </Button>
            </Group>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
